import { db, storage } from "../firebase-config";
import { getDoc, setDoc, deleteField, doc, updateDoc, onSnapshot } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserStatus } from "../components/routes/routing_statuses/RoutingStatuses";

export const organizationSnapshotListener = (organizationId, onOrganizationUpdate) => {
    const docRef = doc(db, "organizations", organizationId);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
            const org = { id: docSnapshot.id, ...docSnapshot.data() };
            onOrganizationUpdate(org);
        } else onOrganizationUpdate(UserStatus.DOES_NOT_EXIST);
    });
    return unsubscribe;
};

export const getOrganization = async(organization) => {
    var docRef = doc(db, "organizations", organization);
    return await getDoc(docRef)
}

export const setOrganizationProcessing = async (organization) => {
    const docRef = doc(db, "organizations", organization);
    await updateDoc(docRef, {
        processing: true
    });
}

// Add a new organization
export const addOrganization = async (organizationDetails) => {
    const { companyName, companyAddress, companyCity, companyState, companyZip, userUid } = organizationDetails;
    const docRef = doc(db, "organizations", companyName);
    try {
        await setDoc(docRef, {
            name: companyName,
            address: companyAddress,
            city: companyCity,
            state: companyState,
            zip: companyZip,
            purchased_features: [],
            job_codes: {},
            user_roles: {
                [userUid]: "Owner",
            },
        }, { merge: true });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error adding organization:", error);
        return { success: false, error: error.message };
    }
};

// Update organization logo
export const updateOrganizationLogo = async (organization, file) => {
    try { 
        const storageRef = ref(storage, `organizations/${organization}/logo`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        const userDocRef = doc(db, "organizations", organization);
        await updateDoc(userDocRef, {
            logo_url: downloadURL
        });

        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating organization logo:", error);
        return { success: false, error: error.message };
    }
};

// Update organization fields
export const updateOrganization = async (orgId, updatedFields) => {
    try {
        const orgDocRef = doc(db, "organizations", orgId);
        await updateDoc(orgDocRef, updatedFields);
        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating organization:", error);
        return { success: false, error: error.message };
    }
};

// Update subscribed users
export const updateSubscribedUsers = async (organization, userId, employeeRole, subscribe) => {
    const docRef = doc(db, 'organizations', organization);
    try {
        if (subscribe) {
            await updateDoc(docRef, {
                [`subscribed_users.${userId}`]: employeeRole
            });
        } else {
            await updateDoc(docRef, {
                [`subscribed_users.${userId}`]: deleteField()
            });
        }
        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating subscribed users:", error);
        return { success: false, error: error.message };
    }
};

// Create a new role with default permissions
export const updateRoles = async (organizationId, roleName) => {
    const docRef = doc(db, 'organizations', organizationId);
    const permissions = {
        assets: {
            read: false,
            write: false
        },
        biziboard: {
            read: false,
            write: false
        },
        bizidocs: {
            read: false,
            write: false
        },
        calendar: {
            read: false,
            write: false
        },
        customers: {
            read: false,
            write: false
        },
        // Employees group
        employees: {
            read: false,
            write: false
        },
        departments: {
            read: false,
            write: false
        },
        performance_reviews: {
            modify_review_templates: false,
            read_all_reviews: false,
            read_and_write_all_reviews: false,
            read_and_write_reviews_for_managed_employees: false
        },
        // Inventory group
        inventory: {
            read: false,
            write: false
        },
        inventory_orders: {
            read: false,
            write: false
        },
        suppliers: {
            read: false,
            write: false
        },
        unit_of_measure: {
            read: false,
            write: false
        },
        // Invoices group
        invoices: {
            read: false,
            send_invoice: false,
            write: false
        },
        invoice_dashboard: {
            read: false,
            write: false
        },
        invoice_settings: {
            read: false,
            write: false
        },
        products: {
            read: false,
            write: false
        },
        invoice_settings: {
            read: false,
            write: false
        },
        // Other modules
        locations: {
            read: false,
            write: false
        },
        organization: {
            read: true,
            write: false
        },
        roles: {
            read: false,
            write: false
        },
        scheduling: {
            read: false,
            write: false
        },
        subscription: {
            read: false,
            write: false
        },
        time_clock: {
            read: false,
            write: false
        },
        review_time_clocks: {
            read_all_time_clocks: false,
            read_and_write_all_time_clocks: false,
            read_and_write_time_clocks_for_managed_employees: false
        }
    };

    try {
        await updateDoc(docRef, {
            [`roles.${roleName}`]: permissions
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error creating role:", error);
        return { success: false, error: error.message };
    }
};

// Update role permissions
export const updateRolePermissions = async (organizationId, roleName, permissions) => {
    const docRef = doc(db, 'organizations', organizationId);
    try {
        await updateDoc(docRef, {
            [`roles.${roleName}`]: permissions
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error updating permissions:", error);
        return { success: false, error: error.message };
    }
};

// Delete a role
export const deleteRole = async (organizationId, roleName) => {
    //TODO: Need to really measure how to implement this. If user's are assigned to role, can't allow delete.
    const docRef = doc(db, 'organizations', organizationId);
    try {
        await updateDoc(docRef, {
            [`roles.${roleName}`]: deleteField()
        });
        return { success: true, error: null };
    } catch (error) {
        console.error("Error deleting role:", error);
        return { success: false, error: error.message };
    }
};